import { MS_PER_DAY, MS_PER_HOUR } from '../../../../constants';
import { AggregationType, Granularity, type SeriesData, type UsageSeriesList } from '../../../../types';

/**
 * Calculates the average usage value and checks if there are enough non-zero days in the series.
 * Returns undefined if fewer than 2 non-zero data points exist (not enough data to show a trend line).
 */
export function calculateAverageUsage(series: SeriesData): number | undefined {
  let total = 0;
  let nonZeroDays = 0;
  let totalDays = 0;

  for (const item of series) {
    if (!!item.value) {
      total += item.value;
      nonZeroDays++;
    }
    // Exclude future days from the total days count
    if (item.value !== null) {
      totalDays++;
    }
  }

  return nonZeroDays >= 2 ? total / totalDays : undefined;
}

/**
 * Returns the next timestamp based on granularity.
 */
function incrementTimestamp(timestamp: number, granularity?: Granularity): number {
  switch (granularity) {
    case Granularity.HOURLY:
      return timestamp + MS_PER_HOUR;
    case Granularity.MONTHLY: {
      const date = new Date(timestamp);
      date.setUTCMonth(date.getUTCMonth() + 1);
      return date.getTime();
    }
    default:
      return timestamp + MS_PER_DAY;
  }
}

/**
 * Generates empty series data between two timestamps (inclusive start, exclusive end).
 */
export function generateDefaultSeries(
  fromTimestampUTC: number,
  toTimestampUTC: number,
  cutoffTimestampUTC: number,
  granularity?: Granularity,
): SeriesData {
  const result: SeriesData = [];
  let current = fromTimestampUTC;

  while (current < toTimestampUTC) {
    const isFuture = current >= cutoffTimestampUTC;
    result.push({
      time: current,
      value: isFuture ? null : 0,
    });
    current = incrementTimestamp(current, granularity);
  }

  return result;
}

export function getNormalizedSeries(
  series: UsageSeriesList['series'],
  fromTimestampUTC: number,
  toTimestampUTC: number,
  cutoffTimestampUTC: number,
  aggregationType: AggregationType,
  metaDataIdx: number = 0,
  granularity?: Granularity,
): SeriesData {
  if (!series?.length) {
    return generateDefaultSeries(fromTimestampUTC, toTimestampUTC, cutoffTimestampUTC, granularity);
  }

  const normalizedSeries = setFutureValuesNull(series, cutoffTimestampUTC, metaDataIdx);
  const result =
    aggregationType === AggregationType.MONTH_TO_DATE
      ? carryForwardCumulativeSeriesValues(normalizedSeries)
      : normalizedSeries;

  // When the backend query `to` is capped (e.g. for AVERAGE), the returned
  // series may not cover the full chart time range. Pad with null entries so
  // the x-axis still extends to toTimestampUTC.
  const lastTime = result.length > 0 ? result[result.length - 1].time : fromTimestampUTC;
  const tailStart = incrementTimestamp(lastTime, granularity);
  const tail = generateDefaultSeries(tailStart, toTimestampUTC, cutoffTimestampUTC, granularity);

  return result.concat(tail);
}

export function setFutureValuesNull(
  points: UsageSeriesList['series'],
  cutoffTimestampUTC: number,
  metaDataIdx: number = 0,
): SeriesData {
  return points.map(({ time, [metaDataIdx.toString()]: value }) => {
    const isFuture = time >= cutoffTimestampUTC;
    const normalizedValue = isFuture ? null : (value ?? 0);
    return {
      time,
      value: normalizedValue,
    };
  });
}

/**
 * Carrys the last non-zero value forward for cumulative series within the same month.
 * This is due to a delay in the data being updated on the backend.
 */
export function carryForwardCumulativeSeriesValues(points: SeriesData): SeriesData {
  let lastValue: number | null = null;

  return points.map((item) => {
    // Keep nulls to avoid rendering them in the chart
    if (item.value === null || item.value === undefined) {
      return { time: item.time, value: null };
    }

    if (item.value > 0) {
      lastValue = item.value;
      return { time: item.time, value: item.value };
    }

    const date = new Date(item.time);
    const startOfMonthUTC = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);

    // Reset the last value to null on the first of the month
    if (item.time === startOfMonthUTC) {
      lastValue = null;
      return { time: item.time, value: item.value };
    }

    return {
      time: item.time,
      value: lastValue !== null ? lastValue : 0,
    };
  });
}

/**
 * Adds primaryContext information to each item in the normalized series.
 * Uses primaryContextKindByTimestamp when available, falling back to primaryContext.
 * Adds primaryContext to each item in the normalized series.
 */
export function addContextToSeries(
  normalizedSeries: SeriesData,
  primaryContextKindByTimestamp: Record<string, string | undefined> | undefined,
): SeriesData {
  return normalizedSeries.map((item) => ({
    ...item,
    primaryContext: primaryContextKindByTimestamp?.[item.time.toString()],
  }));
}

/**
 * Returns the timestamps of the first day of each month at 00:00 UTC,
 * excluding the first and last point.
 */
export function getMonthlyUsageResetTimestamps(series: SeriesData): number[] {
  return series.slice(1, -1).reduce<number[]>((acc, item) => {
    if (item.time) {
      const date = new Date(item.time);
      const startOfMonthUTC = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);

      if (item.time === startOfMonthUTC) {
        acc.push(item.time);
      }
    }
    return acc;
  }, []);
}
